import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthStateService } from './auth-state.service';
import { CompanyConfigService } from './company-config.service';
import { ApiClientService } from './api-client.service';
import { CryptoTokenStorageService } from './crypto-token-storage.service';
import { REQUEST_CONFIGS } from '../config/api-endpoints';
import {
  CompanyTokenData,
  TokenValidationResult
} from '../models/dynamic-company.models';

export interface TokenRefreshResponse {
  data: any;
  Status: boolean;
  Token: string;
  refreshToken?: string;
  access_token_expired_at?: string;
  refresh_token_expired_at?: string;
  message?: string;
}

export interface TokenRefreshRequest {
  userID: number;
  companyId: string;
  userName: string;
}

@Injectable({
  providedIn: 'root'
})
export class TokenService {
  private readonly apiClient = inject(ApiClientService);
  private readonly authStateService = inject(AuthStateService);
  private readonly companyConfigService = inject(CompanyConfigService);
  private readonly cryptoStorage = inject(CryptoTokenStorageService);

  private refreshInProgress = new Map<string, Promise<TokenRefreshResponse>>();
  private lastRefreshTimestamp = new Map<string, number>();
  private lastRefreshResponse = new Map<string, TokenRefreshResponse>();
  private readonly MIN_REFRESH_INTERVAL_MS = 5000;

  private readonly JWT_WARNING_TIME_MINUTES = (environment as any).JWT_EXPIRATION_WARNING_TIME_IN_MINUTES || 5;
  private readonly REFRESH_RETRY_DELAY = 1000;

  getToken(companyName: string): string | null {
    const tokenData = this.authStateService.getCompanyTokenData(companyName);
    return tokenData?.token || null;
  }

  getRefreshToken(_companyName: string): string | null {
    // Refresh token is handled by secure cookie. It is not kept in app storage.
    return null;
  }

  getCurrentToken(): string | null {
    const currentCompany = this.authStateService.currentCompany();
    return currentCompany ? this.getToken(currentCompany) : null;
  }

  validateToken(companyName: string): TokenValidationResult {
    const tokenData = this.authStateService.getCompanyTokenData(companyName);

    if (!tokenData?.token || !tokenData.expiresAt) {
      return {
        isValid: false,
        isExpired: true,
        expiresIn: 0,
        shouldRefresh: false
      };
    }

    const now = Date.now();
    const expiresIn = tokenData.expiresAt.getTime() - now;
    const warningTime = this.JWT_WARNING_TIME_MINUTES * 60 * 1000;

    return {
      isValid: expiresIn > 0,
      isExpired: expiresIn <= 0,
      expiresIn: Math.max(0, expiresIn),
      shouldRefresh: expiresIn > 0 && expiresIn <= warningTime
    };
  }

  isTokenExpired(companyName: string): boolean {
    return this.validateToken(companyName).isExpired;
  }

  shouldRefreshToken(companyName: string): boolean {
    return this.validateToken(companyName).shouldRefresh;
  }

  async refreshToken(companyName: string): Promise<TokenRefreshResponse> {
    const now = Date.now();
    const lastRefresh = this.lastRefreshTimestamp.get(companyName);
    if (lastRefresh && now - lastRefresh < this.MIN_REFRESH_INTERVAL_MS) {
      const cached = this.lastRefreshResponse.get(companyName);
      if (cached) {
        return Promise.resolve(cached);
      }
    }

    const existing = this.refreshInProgress.get(companyName);
    if (existing) {
      return existing;
    }

    const refreshPromise = this.performTokenRefresh(companyName)
      .then(async (result) => {
        this.lastRefreshTimestamp.set(companyName, Date.now());
        this.lastRefreshResponse.set(companyName, result);
        await this.updateTokenData(companyName, result);
        return result;
      })
      .finally(() => {
        this.refreshInProgress.delete(companyName);
      });

    this.refreshInProgress.set(companyName, refreshPromise);
    return refreshPromise;
  }

  private async performTokenRefresh(companyName: string): Promise<TokenRefreshResponse> {
    const authUser = this.authStateService.user();
    const tokenData = this.authStateService.getCompanyTokenData(companyName);

    const userDetailsRaw = localStorage.getItem(`tibss.${companyName}-userDetails`);
    const localUser = userDetailsRaw ? JSON.parse(userDetailsRaw) : null;

    const userID = tokenData?.userId || authUser?.userID || localUser?.userID;
    const userName = authUser?.userName || localUser?.userName || '';

    if (!userID || !userName) {
      throw new Error(`Cannot refresh token: missing user context for ${companyName}`);
    }

    const requestBody: TokenRefreshRequest = {
      userID,
      companyId: companyName,
      userName
    };

    // apiClient.post returns the raw server body directly (not wrapped in ApiResponse).
    // The server response shape matches TokenRefreshResponse: { Status, Token, ... }.
    const response = await firstValueFrom(
      this.apiClient.post<TokenRefreshResponse>(
        `${environment.CAS_BASE_RUL}api/CAS/tokenRefresh`,
        requestBody,
        {
          ...REQUEST_CONFIGS.auth,
          retries: 0,
          timeout: 10000
        }
      )
    ) as unknown as TokenRefreshResponse;

    if (!response?.data?.Status || !response?.data?.Token) {
      throw new Error(response?.data?.message || 'Token refresh failed');
    }

    return response.data;
  }

  private async updateTokenData(companyName: string, response: TokenRefreshResponse): Promise<void> {
    const current = this.authStateService.getCompanyTokenData(companyName);
    const accessExpiry = this.parseJwtExpiry(response.Token) || this.parseBackendDate(response.access_token_expired_at);

    const newTokenData: CompanyTokenData = {
      token: response.Token,
      userId: current?.userId || this.authStateService.getUserId(),
      expiresAt: accessExpiry || new Date(Date.now() + 30 * 60 * 1000),
      refreshToken: current?.refreshToken,
      refreshExpiresAt: current?.refreshExpiresAt
    };

    this.authStateService.updateCompanyToken(companyName, newTokenData);
    this.companyConfigService.updateCompanyToken(companyName, response.Token);

    // Persist encrypted token to IndexedDB so it survives page refresh
    this.cryptoStorage.storeToken(companyName, response.Token, newTokenData.expiresAt, newTokenData.userId).catch(() => {});

    if (this.authStateService.currentCompany() === companyName) {
      this.authStateService.setAuthState({
        token: response.Token,
        lastActivity: new Date(),
        sessionExpiry: newTokenData.expiresAt
      });
    }
  }

  private parseBackendDate(dateStr?: string): Date | null {
    if (!dateStr) return null;

    if (dateStr.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
      const parsed = new Date(dateStr.replace(' ', 'T') + '-05:00');
      if (!isNaN(parsed.getTime())) return parsed;
    }

    const fallback = new Date(dateStr);
    return isNaN(fallback.getTime()) ? null : fallback;
  }

  private parseJwtExpiry(token: string): Date | null {
    try {
      const parts = token.split('.');
      if (parts.length < 2) {
        return null;
      }

      const payload = JSON.parse(this.base64UrlDecode(parts[1])) as { exp?: number };
      if (!payload.exp || typeof payload.exp !== 'number') {
        return null;
      }

      return new Date(payload.exp * 1000);
    } catch {
      return null;
    }
  }

  private base64UrlDecode(value: string): string {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - (normalized.length % 4)) % 4);
    return atob(normalized + padding);
  }

  async refreshTokenWithRetry(companyName: string, maxRetries: number = 3): Promise<boolean> {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        await this.refreshToken(companyName);
        return true;
      } catch (error) {
        if (attempt === maxRetries) {
          return false;
        }
        await this.delay(this.REFRESH_RETRY_DELAY * attempt);
      }
    }
    return false;
  }

  async autoRefreshIfNeeded(companyName: string): Promise<boolean> {
    const validation = this.validateToken(companyName);

    if (validation.isExpired) {
      return false;
    }

    if (validation.shouldRefresh) {
      return this.refreshTokenWithRetry(companyName);
    }

    return true;
  }

  async getValidToken(companyName: string): Promise<string | null> {
    const validation = this.validateToken(companyName);

    if (validation.isExpired) {
      const refreshed = await this.refreshTokenWithRetry(companyName);
      if (!refreshed) return null;
    } else if (validation.shouldRefresh) {
      await this.refreshTokenWithRetry(companyName).catch(() => undefined);
    }

    return this.getToken(companyName);
  }

  validateAllTokens(): Record<string, TokenValidationResult> {
    const allTokens = this.authStateService.getCompanyTokens();
    const results: Record<string, TokenValidationResult> = {};

    Object.keys(allTokens).forEach(companyName => {
      results[companyName] = this.validateToken(companyName);
    });

    return results;
  }

  getExpiredTokenCompanies(): string[] {
    const validations = this.validateAllTokens();
    return Object.keys(validations).filter(company => validations[company].isExpired);
  }

  getCompaniesNeedingRefresh(): string[] {
    const validations = this.validateAllTokens();
    return Object.keys(validations).filter(company => validations[company].shouldRefresh);
  }

  clearAllTokens(): void {
    this.authStateService.setCompanyTokens({});
    this.cryptoStorage.clearAll().catch(() => {});
  }

  storeTokenData(companyName: string, tokenData: CompanyTokenData): void {
    this.authStateService.updateCompanyToken(companyName, tokenData);
    this.companyConfigService.updateCompanyToken(companyName, tokenData.token);
    // Persist encrypted token to IndexedDB
    this.cryptoStorage.storeToken(companyName, tokenData.token, tokenData.expiresAt, tokenData.userId).catch(() => {});
  }

  /**
   * Try to restore a token from encrypted IndexedDB storage.
   * Returns the CompanyTokenData if found and not expired, null otherwise.
   */
  async restoreTokenFromStorage(companyName: string): Promise<CompanyTokenData | null> {
    try {
      const stored = await this.cryptoStorage.getToken(companyName);
      if (!stored) return null;

      // Discard if expired
      if (stored.expiresAt.getTime() <= Date.now()) {
        this.cryptoStorage.removeToken(companyName).catch(() => {});
        return null;
      }

      const tokenData: CompanyTokenData = {
        token: stored.token,
        userId: stored.userId,
        expiresAt: stored.expiresAt
      };

      // Hydrate in-memory state
      this.authStateService.updateCompanyToken(companyName, tokenData);
      this.companyConfigService.updateCompanyToken(companyName, stored.token);

      return tokenData;
    } catch {
      return null;
    }
  }

  async generateRefreshTokenByRefreshToken(companyName: string): Promise<void> {
    await this.refreshToken(companyName);
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  getTimeUntilExpiry(companyName: string): string {
    const validation = this.validateToken(companyName);

    if (validation.isExpired) {
      return 'Expired';
    }

    const minutes = Math.floor(validation.expiresIn / (1000 * 60));
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }

    return `${minutes}m`;
  }

  startTokenMonitoring(): void {
    console.log('Token monitoring started');
  }

  stopTokenMonitoring(): void {
    this.refreshInProgress.clear();
    console.log('Token monitoring stopped');
  }
}
